import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowRight, ShieldCheck, Wallet, Users, BadgeCheck,
  Sparkles, TrendingUp, PhoneCall, MessageCircle, Mail, MapPin,
  Share2, Check,
  Landmark, LineChart, CreditCard, Zap, Wheat, Car, Home,
  UserCog, Store, Smartphone, Code2, Fingerprint, Building2,
  Banknote, Server, GraduationCap, ShoppingBag, IdCard, Lightbulb,
  ShieldPlus,
} from "lucide-react";

/* ─────────────────── 20 Services (website only) ─────────────────── */
const services = [
  { slug: "account-opening",         name: "Account Opening",                 desc: "Savings & Current Account opening — fast & paperless",            icon: Landmark,       grad: "from-emerald-500 to-teal-600",     ring: "linear-gradient(135deg, #10b981, #0d9488)" },
  { slug: "demat-account",           name: "Demat Account Opening",           desc: "Trading + Demat accounts with top brokers",                        icon: LineChart,      grad: "from-violet-500 to-purple-600",    ring: "linear-gradient(135deg, #8b5cf6, #9333ea)" },
  { slug: "credit-card",             name: "Credit Card — Secured & Unsecured", desc: "Instant approvals with top banks",                               icon: CreditCard,     grad: "from-orange-500 to-red-500",       ring: "linear-gradient(135deg, #f97316, #ef4444)" },
  { slug: "instant-loan",            name: "Instant Loan — Personal Loan",    desc: "Pre-approved personal loans in minutes",                           icon: Zap,            grad: "from-amber-500 to-orange-600",     ring: "linear-gradient(135deg, #f59e0b, #ea580c)" },
  { slug: "kcc",                     name: "KCC (Kishan Credit Card)",        desc: "Credit card for farmers — easy agri financing",                   icon: Wheat,          grad: "from-lime-500 to-green-600",       ring: "linear-gradient(135deg, #84cc16, #16a34a)" },
  { slug: "vehicle-loan",            name: "Bike / Car Loan — New & Used",    desc: "Vehicle financing for 2-wheeler & 4-wheeler",                      icon: Car,            grad: "from-sky-500 to-blue-600",         ring: "linear-gradient(135deg, #0ea5e9, #2563eb)" },
  { slug: "home-loan",               name: "Home / Property Loan",            desc: "Home purchase, construction & LAP",                                icon: Home,           grad: "from-rose-500 to-pink-600",        ring: "linear-gradient(135deg, #f43f5e, #db2777)" },
  { slug: "loan-recovery-agent",     name: "Loan Recovery Agent Onboarding",  desc: "Join as a recovery agent — earn per case",                         icon: UserCog,        grad: "from-slate-700 to-slate-900",      ring: "linear-gradient(135deg, #334155, #0f172a)" },
  { slug: "bc-csp",                  name: "BC / CSP Onboarding",             desc: "Become a Business Correspondent / CSP",                            icon: Store,          grad: "from-cyan-500 to-blue-600",        ring: "linear-gradient(135deg, #06b6d4, #2563eb)" },
  { slug: "recharge-app-dev",        name: "Recharge App Development",        desc: "Fully branded Mobile & DTH recharge app",                          icon: Smartphone,     grad: "from-indigo-500 to-blue-600",      ring: "linear-gradient(135deg, #6366f1, #2563eb)" },
  { slug: "recharge-api",            name: "Recharge API",                    desc: "Plug-&-play recharge APIs for your platform",                      icon: Code2,          grad: "from-purple-500 to-fuchsia-600",   ring: "linear-gradient(135deg, #a855f7, #c026d3)" },
  { slug: "aeps-software-dev",       name: "AEPS Software Development",       desc: "Custom AEPS solution with admin panel",                            icon: Fingerprint,    grad: "from-blue-600 to-indigo-700",      ring: "linear-gradient(135deg, #2563eb, #4338ca)" },
  { slug: "aeps-white-label",        name: "AEPS White Label",                desc: "Launch AEPS under your own brand quickly",                         icon: Building2,      grad: "from-teal-500 to-emerald-600",     ring: "linear-gradient(135deg, #14b8a6, #059669)" },
  { slug: "upi-cash-withdrawal",     name: "UPI Cash Withdrawal & Payout API", desc: "Enable UPI payout + cash-out via API",                            icon: Banknote,       grad: "from-green-500 to-emerald-600",    ring: "linear-gradient(135deg, #22c55e, #059669)" },
  { slug: "upi-admin-panel",         name: "UPI Cash Withdrawal Admin Panel", desc: "Full admin software — merchant control & reports",                 icon: Server,         grad: "from-neutral-700 to-stone-900",    ring: "linear-gradient(135deg, #404040, #1c1917)" },
  { slug: "education",               name: "Education",                       desc: "Education financing & student services",                           icon: GraduationCap,  grad: "from-fuchsia-500 to-pink-600",     ring: "linear-gradient(135deg, #d946ef, #db2777)" },
  { slug: "ecommerce",               name: "E-Commerce",                      desc: "Launch your online store — complete e-commerce suite",             icon: ShoppingBag,    grad: "from-pink-500 to-rose-600",        ring: "linear-gradient(135deg, #ec4899, #e11d48)" },
  { slug: "aadhaar-ucl",             name: "Aadhaar UCL",                     desc: "Aadhaar Update Client Lite — authorised onboarding",               icon: IdCard,         grad: "from-red-500 to-orange-600",       ring: "linear-gradient(135deg, #ef4444, #ea580c)" },
  { slug: "uppcl-bijli",             name: "UPPCL — Bijli Bill Collection",   desc: "Authorised electricity bill collection centre",                    icon: Lightbulb,      grad: "from-yellow-500 to-amber-600",     ring: "linear-gradient(135deg, #eab308, #d97706)" },
  { slug: "motor-insurance",         name: "Motor Insurance (Agent/Sub/API)", desc: "Agent, Sub-Agent & API — multiple insurer tie-ups",                icon: ShieldPlus,     grad: "from-blue-500 to-cyan-600",        ring: "linear-gradient(135deg, #3b82f6, #0891b2)" },
];

const stats = [
  { k: "10k+", v: "Merchants Onboarded" },
  { k: "2.5L+", v: "Leads Processed" },
  { k: "₹15Cr", v: "Payouts Enabled" },
  { k: "99.9%", v: "Uptime SLA" },
];

/* ─────────────────── Share Link helpers ─────────────────── */
function shareServiceLink(svc) {
  const url = `${window.location.origin}/s/${svc.slug}`;
  const title = `${svc.name} — EPAYCASH`;
  const text = `${svc.name}: ${svc.desc}\n\nApply with EPAYCASH 👉 ${url}`;
  // Try native share first
  if (navigator.share) {
    navigator.share({ title, text, url }).catch(() => { /* user cancelled */ });
    return "native";
  }
  // No native share — open WhatsApp Web/App
  const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(wa, "_blank", "noopener,noreferrer");
  // Also copy as fallback
  try {
    navigator.clipboard?.writeText(url);
    toast.success("Link WhatsApp me khul gaya + clipboard me copy ho gaya");
  } catch (_) {
    toast.success("WhatsApp open ho raha hai");
  }
  return "whatsapp";
}

async function copyServiceLink(svc) {
  const url = `${window.location.origin}/s/${svc.slug}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copy ho gaya!");
  } catch (_) {
    // Fallback: temp textarea
    const ta = document.createElement("textarea");
    ta.value = url;
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand("copy"); toast.success("Link copy ho gaya!"); }
    catch { toast.error("Copy nahi ho paya — manually copy karein: " + url); }
    document.body.removeChild(ta);
  }
}

/* ─────────────────── Logo (hero + header) ─────────────────── */
function HeroLogo({ size = "lg" }) {
  const h = size === "lg" ? "h-20 md:h-28" : "h-11 md:h-12";
  return (
    <div className="ep-logo-wrap">
      <img
        src="/epaycash-logo.png"
        alt="EPAYCASH — Open Your Savings Account Today"
        className={`ep-logo-img ${h}`}
        data-testid="epaycash-logo"
      />
    </div>
  );
}

/* ─────────────────── Service Card ─────────────────── */
function ServiceCard({ s }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => { await copyServiceLink(s); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div
      className="svc-card group relative bg-white rounded-2xl p-5 border border-slate-200/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
      style={{ "--ring-grad": s.ring }}
      data-testid={`svc-card-${s.slug}`}
    >
      <div className={`svc-ring absolute inset-0 rounded-2xl pointer-events-none`} />
      <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full bg-gradient-to-br ${s.grad} opacity-10 group-hover:opacity-25 transition-opacity duration-500`} />
      <div className="relative">
        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${s.grad} text-white flex items-center justify-center shadow-lg`}>
          <s.icon className="h-7 w-7" strokeWidth={2.2} />
        </div>
        <div className="font-display text-lg font-bold mt-4 text-slate-900 leading-tight">{s.name}</div>
        <div className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.desc}</div>

        {/* Share buttons */}
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => shareServiceLink(s)}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-1.5 bg-gradient-to-r ${s.grad} text-white hover:opacity-90 transition shadow-md`}
            data-testid={`svc-share-${s.slug}`}
            aria-label={`Share ${s.name}`}
          >
            <Share2 className="h-3.5 w-3.5" /> Share Link
          </button>
          <button
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 transition border border-slate-200"
            data-testid={`svc-copy-${s.slug}`}
            aria-label={`Copy ${s.name} link`}
          >
            {copied ? <><Check className="h-3.5 w-3.5 text-emerald-600" /> Copied</> : <>Copy</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Landing ─────────────────── */
export default function Landing() {
  return (
    <div className="min-h-screen text-slate-900 relative overflow-hidden">
      {/* Ambient background blobs */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-hero-gradient" />
      <div className="pointer-events-none absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-[#1a3ba8]/25 blob -z-10" />
      <div className="pointer-events-none absolute top-40 -right-24 h-[420px] w-[420px] rounded-full bg-[#f57320]/25 blob blob-delay-2 -z-10" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-[420px] w-[420px] rounded-full bg-violet-500/20 blob blob-delay-4 -z-10" />

      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-white/40 glass">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3">
          <HeroLogo size="sm" />
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" data-testid="header-login-btn">Login / लॉगिन</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-orange-gradient hover:opacity-90 text-white rounded-full shadow-md shadow-[#f57320]/25" data-testid="header-register-btn">
                Merchant Register <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 glass rounded-full px-3 py-1 text-xs text-slate-700 mb-5">
              <Sparkles className="h-3.5 w-3.5 text-[#f57320]" />
              India&apos;s most-loved BC / DSA platform
            </div>

            {/* Big glowing logo */}
            <div className="mb-6"><HeroLogo size="lg" /></div>

            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] text-slate-900">
              Open Your <span className="bg-clip-text text-transparent bg-brand-gradient">Savings Account</span> Today
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-600 max-w-xl">
              भारत का सबसे भरोसेमंद financial services platform — Merchant और Vendor दोनों के लिए एक ही जगह.
              लीड manage करें, wallet track करें, और business grow करें.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="bg-orange-gradient hover:opacity-90 text-white rounded-full px-6 shadow-lg shadow-[#f57320]/25" data-testid="hero-register-btn">
                  Merchant Register / मर्चेंट रजिस्टर <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full border-[#1a3ba8] text-[#1a3ba8] hover:bg-[#1a3ba8]/5 px-6 bg-white/60" data-testid="hero-login-btn">
                  Login to Dashboard
                </Button>
              </Link>
            </div>
            <div className="mt-7 grid grid-cols-3 gap-4 max-w-md">
              <div className="flex items-center gap-2 text-xs text-slate-700"><ShieldCheck className="h-4 w-4 text-[#1a3ba8]" /> 100% Secure</div>
              <div className="flex items-center gap-2 text-xs text-slate-700"><BadgeCheck className="h-4 w-4 text-[#1a3ba8]" /> Easy Onboarding</div>
              <div className="flex items-center gap-2 text-xs text-slate-700"><Wallet className="h-4 w-4 text-[#1a3ba8]" /> Instant Payouts</div>
            </div>
          </div>

          {/* Right preview card */}
          <div className="relative float-soft">
            <div className="absolute -inset-6 bg-gradient-to-br from-[#1a3ba8]/25 to-[#f57320]/25 rounded-3xl blur-2xl" />
            <div className="relative glass rounded-2xl p-6 md:p-8 shadow-2xl shadow-[#1a3ba8]/20">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">Merchant Dashboard Preview</div>
                <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">Live</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="border rounded-md p-4 bg-white/60"><div className="text-[10px] uppercase tracking-widest text-slate-500">Wallet</div><div className="font-display text-2xl font-semibold text-[#1a3ba8] mt-1">₹ 12,450</div></div>
                <div className="border rounded-md p-4 bg-white/60"><div className="text-[10px] uppercase tracking-widest text-slate-500">Approved Leads</div><div className="font-display text-2xl font-semibold text-emerald-600 mt-1">128</div></div>
                <div className="border rounded-md p-4 bg-white/60"><div className="text-[10px] uppercase tracking-widest text-slate-500">Pending</div><div className="font-display text-2xl font-semibold text-amber-600 mt-1">14</div></div>
                <div className="border rounded-md p-4 bg-white/60"><div className="text-[10px] uppercase tracking-widest text-slate-500">Rejected</div><div className="font-display text-2xl font-semibold text-rose-600 mt-1">6</div></div>
              </div>
              <div className="mt-5 border rounded-md p-4 bg-white/60">
                <div className="text-sm font-medium text-slate-800 mb-2 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600" /> Recent Lead — HDFC Credit Card
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Ramesh K. · 98****4321</span>
                  <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-medium">APPROVED</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats trust bar */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-8">
          <div className="glass rounded-2xl p-4 md:p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div key={s.v} className="text-center md:text-left">
                <div className="font-display text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-brand-gradient">{s.k}</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles — Merchant + Vendor only */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">Roles / भूमिकाएँ</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Built for every layer of your business</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            { name: "Merchant", desc: "Services access करें, leads submit करें, wallet track करें और withdrawals request करें.", grad: "bg-orange-gradient", icon: Store, testid: "role-merchant" },
            { name: "Vendor",   desc: "Team बनाएँ, merchants add करें, leads track करें और complete reports देखें.",       grad: "bg-violet-gradient", icon: Users, testid: "role-vendor" },
          ].map((r) => (
            <div key={r.name} className="stat-card bg-white/85 backdrop-blur-md border rounded-2xl p-6 relative overflow-hidden" data-testid={r.testid}>
              <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full ${r.grad} opacity-10`} />
              <div className={`w-12 h-12 rounded-xl ${r.grad} text-white flex items-center justify-center shadow-md`}>
                <r.icon className="h-6 w-6" />
              </div>
              <div className="font-display text-xl font-bold mt-4">{r.name}</div>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{r.desc}</p>
              <Link to="/register" className="inline-flex items-center gap-1 text-sm font-semibold text-[#1a3ba8] mt-4 hover:gap-2 transition-all">
                Register as {r.name} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Services Grid — colorful + Share Link */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10 pb-20" id="services">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">Our Services / सेवाएँ</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">All the services your business needs</h2>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">Finance, Loans, Cards, Insurance, Software — एक छत के नीचे. Share Link button se WhatsApp par instant share करें.</p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs text-slate-500">
            <Share2 className="h-4 w-4" /> Tap <b className="text-slate-700">Share Link</b> on any card
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {services.map((s) => <ServiceCard key={s.slug} s={s} />)}
        </div>
      </section>

      {/* Support strip */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="glass rounded-2xl p-6 md:p-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-gradient text-white flex items-center justify-center"><PhoneCall className="h-5 w-5" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Customer Care</div>
              <div className="font-medium mt-1">+91 87965 93086</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-gradient text-white flex items-center justify-center"><MessageCircle className="h-5 w-5" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">WhatsApp</div>
              <div className="font-medium mt-1">+91 87965 93086</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-gradient text-white flex items-center justify-center"><Mail className="h-5 w-5" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Email</div>
              <div className="font-medium mt-1">support@epaycash.in</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-gradient text-white flex items-center justify-center"><MapPin className="h-5 w-5" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Office</div>
              <div className="font-medium mt-1">Noida, UP — 201309</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid md:grid-cols-2 gap-6 items-center relative">
          <div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">आज ही शुरू करें अपना financial business</h3>
            <p className="mt-3 text-white/85 max-w-lg">Free merchant onboarding. Manual KYC. Wallet + Withdrawals. सब कुछ एक ही dashboard पर.</p>
          </div>
          <div className="flex md:justify-end">
            <Link to="/register">
              <Button size="lg" className="bg-orange-gradient hover:opacity-90 text-white rounded-full px-6 shadow-lg shadow-[#f57320]/30" data-testid="cta-register-btn">
                Register Now <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t bg-white/70 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex items-center justify-between text-xs text-slate-500">
          <span>© {new Date().getFullYear()} EPAYCASH · All rights reserved.</span>
          <span>Made with ♥ in India</span>
        </div>
      </footer>
    </div>
  );
}
